import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import TopNav from "../components/TopNav";
import BackButton from "../components/BackButton";
import "./NetworkMap.css";

// Fix leaflet default icon path issue with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
    iconUrl:       "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
    shadowUrl:     "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Escape special XML characters in a string value.
 */
function escXml(val) {
    if (val == null) return "";
    return String(val)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&apos;");
}

/**
 * Convert the raw routes array (response.data.routes) to an XML string.
 * Each route object becomes a <route> element; every key becomes a child element.
 */
function buildXml(routes) {
    if (!Array.isArray(routes) || routes.length === 0) {
        return '<?xml version="1.0" encoding="UTF-8"?>\n<network>\n</network>';
    }

    const lines = ['<?xml version="1.0" encoding="UTF-8"?>', '<network>'];

    routes.forEach((r) => {
        lines.push("  <route>");
        Object.entries(r).forEach(([key, value]) => {
            // Skip internal underscore-prefixed keys added by parseNetworkData
            if (key.startsWith("_")) return;
            lines.push(`    <${escXml(key)}>${escXml(value)}</${escXml(key)}>`);
        });
        lines.push("  </route>");
    });

    lines.push("</network>");
    return lines.join("\n");
}

/** Build the airports lookup + routes list from the raw API payload. */
function parseNetworkData(routes) {
    const airports = {};
    const routeList  = [];

    if (!Array.isArray(routes)) return { airports, routeList };

    routes.forEach((r) => {
        if (!r.orig || !r.dest) return;

        // Support both field name conventions:
        //   origlat/origlon/destlat/destlon  (live API)
        //   olat/olon/dlat/dlon              (legacy)
        const olat = parseFloat(r.origlat ?? r.olat ?? 0);
        const olon = parseFloat(r.origlon ?? r.olon ?? 0);
        const dlat = parseFloat(r.destlat ?? r.dlat ?? 0);
        const dlon = parseFloat(r.destlon ?? r.dlon ?? 0);

        if (olat && olon) airports[r.orig] = { icao: r.orig, lat: olat, lon: olon };
        if (dlat && dlon) airports[r.dest] = { icao: r.dest, lat: dlat, lon: dlon };

        routeList.push({ ...r, _olat: olat, _olon: olon, _dlat: dlat, _dlon: dlon });
    });

    return { airports, routeList };
}

/** Approximate geodesic polyline by interpolating N intermediate waypoints. */
function geodesicPoints(lat1, lon1, lat2, lon2, steps = 32) {
    const toRad = (d) => (d * Math.PI) / 180;
    const toDeg = (r) => (r * 180) / Math.PI;

    const φ1 = toRad(lat1), λ1 = toRad(lon1);
    const φ2 = toRad(lat2), λ2 = toRad(lon2);

    const d = 2 * Math.asin(
        Math.sqrt(
            Math.sin((φ2 - φ1) / 2) ** 2 +
            Math.cos(φ1) * Math.cos(φ2) * Math.sin((λ2 - λ1) / 2) ** 2
        )
    );

    if (d === 0) return [[lat1, lon1]];

    const pts = [];
    for (let i = 0; i <= steps; i++) {
        const f = i / steps;
        const A = Math.sin((1 - f) * d) / Math.sin(d);
        const B = Math.sin(f * d) / Math.sin(d);
        const x = A * Math.cos(φ1) * Math.cos(λ1) + B * Math.cos(φ2) * Math.cos(λ2);
        const y = A * Math.cos(φ1) * Math.sin(λ1) + B * Math.cos(φ2) * Math.sin(λ2);
        const z = A * Math.sin(φ1) + B * Math.sin(φ2);
        pts.push([toDeg(Math.atan2(z, Math.sqrt(x * x + y * y))), toDeg(Math.atan2(y, x))]);
    }
    return pts;
}

/**
 * Split an array of [lat, lon] points into sub-arrays that don't cross the
 * antimeridian.  A crossing is detected when consecutive longitudes differ
 * by more than 180°, which is what causes Leaflet to draw a horizontal line
 * spanning the entire map instead of a short arc across the date line.
 */
function splitAtAntimeridian(pts) {
    if (pts.length < 2) return [pts];

    const segments = [];
    let current = [pts[0]];

    for (let i = 1; i < pts.length; i++) {
        const prevLon = pts[i - 1][1];
        const currLon = pts[i][1];

        if (Math.abs(currLon - prevLon) > 180) {
            // Interpolate the crossing latitude at ±180
            const dLon    = currLon - prevLon + (currLon < prevLon ? 360 : -360);
            const ratio   = (prevLon > 0 ? 180 - prevLon : -180 - prevLon) / dLon;
            const crossLat = pts[i - 1][0] + ratio * (pts[i][0] - pts[i - 1][0]);

            // End current segment exactly at the boundary
            current.push([crossLat, prevLon > 0 ? 180 : -180]);
            segments.push(current);

            // Start a new segment from the other side of the boundary
            current = [[crossLat, currLon > 0 ? 180 : -180], pts[i]];
        } else {
            current.push(pts[i]);
        }
    }

    segments.push(current);
    return segments;
}

// ---------------------------------------------------------------------------
// Custom airport marker icon
// ---------------------------------------------------------------------------
function makeAirportIcon(selected, darkMode) {
    const fill   = selected ? "#f97316" : (darkMode ? "#38bdf8" : "#465fa4");
    const stroke = darkMode ? "#0f172a" : "#ffffff";
    const svg = `
        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28">
            <circle cx="14" cy="14" r="10" fill="${fill}" stroke="${stroke}" stroke-width="2.5"/>
            <circle cx="14" cy="14" r="4"  fill="${stroke}"/>
        </svg>`;
    return L.divIcon({
        html: svg,
        className: "",
        iconSize:   [28, 28],
        iconAnchor: [14, 14],
        popupAnchor: [0, -16],
    });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const NetworkMap = (props) => {
    const [data,            setData]            = useState([]);
    const [loading,         setLoading]         = useState(false);
    const [error,           setError]           = useState(null);
    // Multi-select: Set of selected ICAO strings (React state for rendering)
    const [selectedApts,    setSelectedApts]    = useState(new Set());
    const [routeModal,      setRouteModal]      = useState(null);
    // Map of icao → route[] (kept for potential future use / stat pills)
    const [aptRoutesBySrc,  setAptRoutesBySrc]  = useState({});
    const [mapReady,        setMapReady]        = useState(false);

    const darkMode = props.darkmode;

    const mapContainerRef  = useRef(null);
    const mapRef           = useRef(null);
    const markersRef       = useRef({});   // icao → L.Marker
    // Per-airport line buckets: { [icao]: L.Polyline[] }
    const linesRef         = useRef({});
    const secLinesRef      = useRef({});
    const networkRef       = useRef({ airports: {}, routeList: [] });
    // Ref-copy of selectedApts Set so callbacks always see current value
    const selectedAptsRef  = useRef(new Set());

    // ------------------------------------------------------------------
    // Data fetch
    // ------------------------------------------------------------------
    useEffect(() => {
        getData();
        return () => {};
    }, []);

    const getData = async () => {
        try {
            console.log("getData()");
            setLoading(true);
            const response = await axios.get("/api/network-map", {
                params: { _t: Date.now() },
            });
            console.log(response.data.routes);
            setData(response.data.routes);
        } catch (e) {
            console.log(e);
            setError(e);
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    // ------------------------------------------------------------------
    // Initialise Leaflet map once
    // ------------------------------------------------------------------
    useEffect(() => {
        if (!mapContainerRef.current || mapRef.current) return;

        const tileUrl = darkMode
            ? "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
            : "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png";

        const defaultCenter = [35.0421, -89.9763];
        const defaultZoom   = 5;

        const map = L.map(mapContainerRef.current, {
            center:  defaultCenter,
            zoom:    defaultZoom,
            minZoom: 2,
            maxZoom: 14,
            zoomControl: true,
        });

        L.tileLayer(tileUrl, {
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        }).addTo(map);

        mapRef.current = map;
        setMapReady(true);

        return () => {
            map.remove();
            mapRef.current = null;
        };
    }, []);   // intentionally run once

    // ------------------------------------------------------------------
    // Rebuild markers whenever data or darkMode changes
    // ------------------------------------------------------------------
    useEffect(() => {
        if (!mapReady || !mapRef.current) return;

        const map = mapRef.current;

        // Clear old markers
        Object.values(markersRef.current).forEach((m) => map.removeLayer(m));
        markersRef.current = {};

        // Clear all lines for every airport
        Object.values(linesRef.current).forEach((arr) => arr.forEach((l) => map.removeLayer(l)));
        linesRef.current = {};
        Object.values(secLinesRef.current).forEach((arr) => arr.forEach((l) => map.removeLayer(l)));
        secLinesRef.current = {};

        const emptySet = new Set();
        selectedAptsRef.current = emptySet;
        setSelectedApts(emptySet);
        setAptRoutesBySrc({});

        const { airports, routeList } = parseNetworkData(data);
        networkRef.current = { airports, routeList };

        Object.values(airports).forEach((apt) => {
            if (!apt.lat || !apt.lon) return;

            const marker = L.marker([apt.lat, apt.lon], {
                icon:  makeAirportIcon(false, darkMode),
                title: apt.icao,
            }).addTo(map);

            marker.bindTooltip(apt.icao, {
                permanent:  false,
                direction:  "top",
                className:  "nm-map-tooltip",
                offset:     [0, -16],
            });

            marker.on("click", () => handleAirportClick(apt.icao));
            markersRef.current[apt.icao] = marker;
        });

        const userIcao = props.location ? String(props.location).toUpperCase() : null;
        if (userIcao && airports[userIcao]) {
            const { lat, lon } = airports[userIcao];
            map.setView([lat, lon], 5);
        }
    }, [data, mapReady, darkMode]);

    // ------------------------------------------------------------------
    // XML download handler
    // ------------------------------------------------------------------
    const handleDownloadXml = useCallback(() => {
        const xml = buildXml(data);
        const blob = new Blob([xml], { type: "application/xml;charset=utf-8" });
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement("a");
        a.href     = url;
        a.download = "fdxva_network.xml";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }, [data]);

    // ------------------------------------------------------------------
    // Helper: remove lines drawn for a specific airport
    // ------------------------------------------------------------------
    const clearLinesForAirport = useCallback((icao) => {
        const map = mapRef.current;
        if (!map) return;
        (linesRef.current[icao] || []).forEach((l) => map.removeLayer(l));
        delete linesRef.current[icao];
        (secLinesRef.current[icao] || []).forEach((l) => map.removeLayer(l));
        delete secLinesRef.current[icao];
    }, []);

    // ------------------------------------------------------------------
    // Clear-all-routes handler (button)
    // ------------------------------------------------------------------
    const handleClearRoutes = useCallback(() => {
        const map = mapRef.current;
        if (!map) return;
        Object.values(linesRef.current).forEach((arr) => arr.forEach((l) => map.removeLayer(l)));
        linesRef.current = {};
        Object.values(secLinesRef.current).forEach((arr) => arr.forEach((l) => map.removeLayer(l)));
        secLinesRef.current = {};
        Object.values(markersRef.current).forEach((marker) =>
            marker.setIcon(makeAirportIcon(false, props.darkmode))
        );
        const emptySet = new Set();
        selectedAptsRef.current = emptySet;
        setSelectedApts(emptySet);
        setAptRoutesBySrc({});
    }, [props.darkmode]);

    // ------------------------------------------------------------------
    // Airport click handler — supports multiple simultaneous selections
    // ------------------------------------------------------------------
    const handleAirportClick = useCallback((icao) => {
        const map = mapRef.current;
        if (!map) return;

        const { airports, routeList } = networkRef.current;
        const currentSelected = selectedAptsRef.current;

        if (currentSelected.has(icao)) {
            // Deselect this airport: remove its lines and reset its marker
            clearLinesForAirport(icao);
            if (markersRef.current[icao]) {
                markersRef.current[icao].setIcon(makeAirportIcon(false, props.darkmode));
            }
            const next = new Set(currentSelected);
            next.delete(icao);
            selectedAptsRef.current = next;
            setSelectedApts(new Set(next));
            setAptRoutesBySrc((prev) => {
                const updated = { ...prev };
                delete updated[icao];
                return updated;
            });
            return;
        }

        // Select this airport: highlight marker, draw lines, keep existing selections
        if (markersRef.current[icao]) {
            markersRef.current[icao].setIcon(makeAirportIcon(true, props.darkmode));
        }

        // Primary routes from this airport
        const origRoutes = routeList.filter((r) => r.orig === icao);

        // Collect destination ICAOs for secondary lines
        const destIcaos = new Set(origRoutes.map((r) => r.dest));

        // Draw primary route lines
        linesRef.current[icao] = [];
        origRoutes.forEach((r) => {
            const oApt = airports[r.orig];
            const dApt = airports[r.dest];
            if (!oApt || !dApt) return;

            const pts      = geodesicPoints(
                r._olat || oApt.lat, r._olon || oApt.lon,
                r._dlat || dApt.lat, r._dlon || dApt.lon,
                48
            );
            const segments = splitAtAntimeridian(pts);
            segments.forEach((seg) => {
                const line = L.polyline(seg, {
                    color:     "#f97316",
                    weight:    2.5,
                    opacity:   0.80,
                    dashArray: "0",
                }).addTo(map);

                line.on("click", (e) => {
                    L.DomEvent.stopPropagation(e);
                    setRouteModal(r);
                });

                linesRef.current[icao].push(line);
            });
        });

        // Draw secondary (onward) route lines
        secLinesRef.current[icao] = [];
        destIcaos.forEach((destIcao) => {
            const onwardRoutes = routeList.filter(
                (r) => r.orig === destIcao && r.dest !== icao
            );
            onwardRoutes.forEach((r) => {
                const oApt = airports[r.orig];
                const dApt = airports[r.dest];
                if (!oApt || !dApt) return;

                const pts      = geodesicPoints(
                    r._olat || oApt.lat, r._olon || oApt.lon,
                    r._dlat || dApt.lat, r._dlon || dApt.lon,
                    32
                );
                const segments = splitAtAntimeridian(pts);
                segments.forEach((seg) => {
                    const secLine = L.polyline(seg, {
                        color:     "#94a3b8",
                        weight:    1.2,
                        opacity:   0.45,
                        dashArray: "5, 6",
                    }).addTo(map);

                    secLine.on("click", (e) => {
                        L.DomEvent.stopPropagation(e);
                        setRouteModal(r);
                    });

                    secLinesRef.current[icao].push(secLine);
                });
            });
        });

        const next = new Set(currentSelected);
        next.add(icao);
        selectedAptsRef.current = next;
        setSelectedApts(new Set(next));
        setAptRoutesBySrc((prev) => ({ ...prev, [icao]: origRoutes }));
    }, [props.darkmode, clearLinesForAirport]);

    // ------------------------------------------------------------------
    // Sync marker icons when darkMode toggles
    // ------------------------------------------------------------------
    useEffect(() => {
        Object.entries(markersRef.current).forEach(([icao, marker]) => {
            marker.setIcon(makeAirportIcon(selectedAptsRef.current.has(icao), darkMode));
        });
        if (mapRef.current) {
            mapRef.current.eachLayer((layer) => {
                if (layer instanceof L.TileLayer) {
                    mapRef.current.removeLayer(layer);
                }
            });
            const tileUrl = darkMode
                ? "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                : "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png";
            L.tileLayer(tileUrl, {
                attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
            }).addTo(mapRef.current);
        }
    }, [darkMode]);

    // ------------------------------------------------------------------
    // Derived stats
    // ------------------------------------------------------------------
    const { airports, routeList } = networkRef.current;
    const aptCount    = Object.keys(airports).length;
    const routeCount  = routeList.length;
    const hasSelection = selectedApts.size > 0;
    // Total routes shown across all selected airports
    const totalShownRoutes = Object.values(aptRoutesBySrc).reduce((s, arr) => s + arr.length, 0);

    // ------------------------------------------------------------------
    // Render
    // ------------------------------------------------------------------
    return (
        <React.Fragment>
            <TopNav page={props.page} darkMode={props.darkmode} />
            <BackButton />

            <div className={`nm-page ${darkMode ? "nm-dark" : "nm-light"}`}>

                {/* ── Page Header ── */}
                <header className="nm-header">
                    <div className="nm-header-left">
                        {/* <div className="nm-header-icon" aria-hidden="true">✈</div> */}
                        <div>
                            <h1 className="nm-title">Network Map</h1>
                            <p className="nm-subtitle">FedEx Virtual Airlines · Global Route Network</p>
                        </div>
                    </div>

                    <div className="nm-stats-row">
                        <div className="nm-stat-pill" tabIndex="1">
                            <span className="nm-stat-value">{aptCount}</span>
                            <span className="nm-stat-label">Airports</span>
                        </div>
                        <div className="nm-stat-pill" tabIndex="2">
                            <span className="nm-stat-value">{routeCount}</span>
                            <span className="nm-stat-label">Routes</span>
                        </div>
                        {hasSelection && (
                            <div className="nm-stat-pill nm-stat-active" tabIndex="3">
                                {selectedApts.size === 1 ? (
                                    <>
                                        <span className="nm-stat-value">{[...selectedApts][0]}</span>
                                        <span className="nm-stat-label">{totalShownRoutes} routes</span>
                                    </>
                                ) : (
                                    <>
                                        <span className="nm-stat-value">{selectedApts.size} airports</span>
                                        <span className="nm-stat-label">{totalShownRoutes} routes</span>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </header>

                {/* ── Loading Banner ── */}
                {loading && (
                    <div className="nm-loading-bar" role="status" aria-live="polite">
                        <div className="nm-loading-shimmer" />
                        <span>Loading network data…</span>
                    </div>
                )}

                {/* ── Error Banner ── */}
                {error && !loading && (
                    <div className="nm-error-banner" role="alert">
                        <span className="nm-error-icon">⚠</span>
                        <span>Unable to load network data. Please try again later.</span>
                        <button
                            className="nm-retry-btn"
                            onClick={getData}
                            tabIndex="4"
                        >
                            Retry
                        </button>
                    </div>
                )}

                {/* ── Main Layout ── */}
                <div className="nm-layout">

                    {/* Map card */}
                    <div className="nm-map-card">
                        {loading && !mapReady && (
                            <div className="nm-map-loading">
                                <div className="nm-map-loading-pulse" />
                                <p>Initialising map…</p>
                            </div>
                        )}
                        <div
                            ref={mapContainerRef}
                            className="nm-map-container"
                            aria-label="Network map"
                        />
                        {/* Clear Routes overlay button — shown whenever any airport is selected */}
                        {hasSelection && (
                            <button
                                className="nm-clear-routes-btn"
                                onClick={handleClearRoutes}
                                aria-label="Clear all routes from map"
                                title="Clear all routes"
                            >
                                ✕ Clear routes
                            </button>
                        )}
                    </div>

                </div>

                {/* ── Download bar ── */}
                <div className="nm-download-bar">
                    <button
                        className="nm-download-btn"
                        onClick={handleDownloadXml}
                        disabled={!data || data.length === 0}
                        aria-label="Download network data as XML"
                    >
                    XML
                    </button>
                    {data && data.length > 0 && (
                        <span className="nm-download-info">
                            {data.length} route{data.length !== 1 ? "s" : ""} · fdxva_network.xml
                        </span>
                    )}
                </div>

            </div>

            {/* ── Route Detail Modal ── */}
            {routeModal && (
                <div
                    className="nm-modal-overlay"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Route details"
                    onClick={() => setRouteModal(null)}
                >
                    <div
                        className={`nm-modal ${darkMode ? "nm-dark" : "nm-light"}`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Modal header */}
                        <div className="nm-modal-header">
                            <div className="nm-modal-title-row">
                                <span className="nm-modal-icon" aria-hidden="true">✈</span>
                                <h2 className="nm-modal-title">
                                    {routeModal.orig}
                                    <span className="nm-modal-arrow"> → </span>
                                    {routeModal.dest}
                                </h2>
                            </div>
                            <button
                                className="nm-modal-close"
                                onClick={() => setRouteModal(null)}
                                aria-label="Close route details"
                                tabIndex="0"
                            >
                                ✕
                            </button>
                        </div>

                        {/* Modal body */}
                        <div className="nm-modal-body">
                            <div className="nm-modal-grid">
                                {(routeModal.origname || routeModal.oapt) && (
                                    <div className="nm-modal-field">
                                        <span className="nm-modal-label">Origin Airport</span>
                                        <span className="nm-modal-value">{routeModal.origname || routeModal.oapt}</span>
                                    </div>
                                )}
                                {(routeModal.destname || routeModal.dapt) && (
                                    <div className="nm-modal-field">
                                        <span className="nm-modal-label">Destination Airport</span>
                                        <span className="nm-modal-value">{routeModal.destname || routeModal.dapt}</span>
                                    </div>
                                )}
                                {(routeModal.distance || routeModal.nm) != null && (
                                    <div className="nm-modal-field">
                                        <span className="nm-modal-label">Distance</span>
                                        <span className="nm-modal-value">
                                            {Math.round(routeModal.distance || routeModal.nm)} nm
                                        </span>
                                    </div>
                                )}
                                {routeModal.ete != null && (
                                    <div className="nm-modal-field">
                                        <span className="nm-modal-label">Est. Flight Time</span>
                                        <span className="nm-modal-value">{routeModal.ete} hrs</span>
                                    </div>
                                )}
                                {routeModal.aircraft && (
                                    <div className="nm-modal-field">
                                        <span className="nm-modal-label">Aircraft</span>
                                        <span className="nm-modal-value">{routeModal.aircraft}</span>
                                    </div>
                                )}
                                {routeModal.flight_number && (
                                    <div className="nm-modal-field">
                                        <span className="nm-modal-label">Flight #</span>
                                        <span className="nm-modal-value">{routeModal.flight_number}</span>
                                    </div>
                                )}
                                {routeModal.total_flights != null && (
                                    <div className="nm-modal-field">
                                        <span className="nm-modal-label">Total Flights</span>
                                        <span className="nm-modal-value">{routeModal.total_flights}</span>
                                    </div>
                                )}
                                {routeModal.last_flown && (
                                    <div className="nm-modal-field">
                                        <span className="nm-modal-label">Last Flown</span>
                                        <span className="nm-modal-value">{routeModal.last_flown}</span>
                                    </div>
                                )}
                                {routeModal.alternate && (
                                    <div className="nm-modal-field">
                                        <span className="nm-modal-label">Alternate</span>
                                        <span className="nm-modal-value">{routeModal.alternate}</span>
                                    </div>
                                )}
                            </div>

                            {(routeModal.route || routeModal.fplan) && (
                                <div className="nm-modal-fplan">
                                    <span className="nm-modal-label">Route / Flight Plan</span>
                                    <code className="nm-modal-fplan-text">{routeModal.route || routeModal.fplan}</code>
                                </div>
                            )}
                        </div>

                        <div className="nm-modal-footer">
                            <button
                                className="nm-modal-btn-close"
                                onClick={() => setRouteModal(null)}
                                tabIndex="0"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </React.Fragment>
    );
};

export default NetworkMap;
